import type { ChangeEvent, FC } from 'react';

import { cn } from '@/utils/cn.util';
import type {
  CampusFilterOption,
  StudentListFilter,
  StudentListSort,
} from '@/types/student.types';

interface UsersToolbarProps {
  filter: StudentListFilter;
  campusId: string;
  sort: StudentListSort;
  search: string;
  campuses: CampusFilterOption[];
  onChangeFilter: (filter: StudentListFilter) => void;
  onChangeCampus: (campusId: string) => void;
  onChangeSort: (sort: StudentListSort) => void;
  onChangeSearch: (search: string) => void;
}

const TABS: ReadonlyArray<{ key: StudentListFilter; label: string }> = [
  { key: 'all', label: 'All Users' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'reported', label: 'Reported' },
];

const SORTS: ReadonlyArray<{ key: StudentListSort; label: string }> = [
  { key: 'newest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'trustDesc', label: 'Trust ↓' },
  { key: 'trustAsc', label: 'Trust ↑' },
];

const chipBase =
  'cursor-pointer rounded-full border border-[var(--color-divider)] px-3 py-1.5 text-[11.5px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:border-brand-500 hover:bg-brand-500 hover:text-white';
const chipActive = 'border-brand-500 bg-brand-500 text-white hover:bg-brand-500';

const selectStyles =
  'h-8 cursor-pointer appearance-none rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-3 pr-8 text-[12.5px] font-medium text-[var(--color-ink-muted)] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';

const UsersToolbar: FC<UsersToolbarProps> = ({
  filter,
  campusId,
  sort,
  search,
  campuses,
  onChangeFilter,
  onChangeCampus,
  onChangeSort,
  onChangeSearch,
}) => {
  const handleCampus = (e: ChangeEvent<HTMLSelectElement>): void => onChangeCampus(e.target.value);
  const handleSort = (e: ChangeEvent<HTMLSelectElement>): void =>
    onChangeSort(e.target.value as StudentListSort);
  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => onChangeSearch(e.target.value);

  return (
    <div className="flex flex-col gap-3 border-b border-[var(--color-divider)] px-4 py-3 md:flex-row md:flex-wrap md:items-center md:px-5">
      <div className="flex flex-wrap gap-1.5">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={cn(chipBase, filter === tab.key && chipActive)}
            onClick={() => onChangeFilter(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 md:ml-auto">
        <div className="relative w-full sm:w-56">
          <input
            type="search"
            value={search}
            onChange={handleSearch}
            placeholder="🔍 Search name or email…"
            className="h-8 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-3 text-[12.5px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-soft)] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <div className="relative">
          <select value={campusId} onChange={handleCampus} className={selectStyles}>
            <option value="">🔽 All Campuses</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--color-ink-soft)]">
            ▼
          </span>
        </div>

        <div className="relative">
          <select value={sort} onChange={handleSort} className={selectStyles}>
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                ↕️ Sort: {s.label}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--color-ink-soft)]">
            ▼
          </span>
        </div>
      </div>
    </div>
  );
};

export default UsersToolbar;
